import {useEffect, useMemo, useState} from 'react';
import {formatDobShort} from '../../constants/designTokens';
import {
  plannerPatientIdentityKeys,
  plannerPatientMetadataText,
  type PlannerPatient,
} from './plannerModels';
import {normalizePatientNameParts} from '../../utils/patientNames';
import {
  fetchPlannerLinkedLocalPatientsByEhrIds,
  fetchPlannerLocalPatientsByIds,
  resolvePlannerEhrPatients,
} from './plannerPatientHydrationCache';

const hasValue = (value: any) => value != null && String(value).trim() !== '';
const cleanText = (value: any) => String(value ?? '').trim();

const normalizeEhrId = (value: any) =>
  cleanText(value).split('/').filter(Boolean).pop() || '';

const firstValue = (...values: any[]) =>
  values.find(value => hasValue(value)) ?? '';

const patientLanguageFromRecord = (record: Record<string, any> = {}) =>
  firstValue(
    record.language,
    record.preferred_language,
    record.preferredLanguage,
  );

const canonicalizePlannerPatientName = (
  patient: PlannerPatient,
): PlannerPatient => {
  const name = normalizePatientNameParts(patient);
  return {
    ...patient,
    firstName: name.firstName,
    middleName: name.middleName,
    lastName: name.lastName,
    displayName: name.displayName,
    fullName: name.fullName,
  };
};

const canonicalMatchKind = (patient: PlannerPatient) => {
  if (patient.localId && (patient.ehrId || patient.linkedEhrId)) {
    return 'linked';
  }
  if (patient.localId) return 'localOnly';
  if (patient.ehrId) return 'ehrOnly';
  return 'unknown';
};

const ehrIdFromPlannerPatient = (patient: any) => {
  const direct =
    patient?.ehrId ||
    patient?.linkedEhrId ||
    patient?.ehrSourceId ||
    patient?.ehr_source_id;
  if (direct) return normalizeEhrId(direct);
  const id = cleanText(patient?.id);
  return id.startsWith('ehr_') ? id.slice('ehr_'.length) : '';
};

export const plannerEventEhrPatientId = (event: any) =>
  ehrIdFromPlannerPatient(event?.patient);

export const plannerEventLocalPatientId = (event: any) => {
  const patient = event?.patient || {};
  const localId = cleanText(patient.localId);
  if (localId) return localId;
  const id = cleanText(patient.id);
  return id.startsWith('local_') ? id.slice('local_'.length) : '';
};

export const plannerEventNeedsLocalPatientHydration = (event: any) => {
  const patient = event?.patient;
  const localId = plannerEventLocalPatientId(event);
  return Boolean(
    patient &&
    localId &&
    (!hasValue(patient.linkedEhrId) ||
      !hasValue(patient.language) ||
      !hasValue(patient.dob) ||
      !hasValue(patient.mrn)),
  );
};

export const plannerEventNeedsPatientHydration = (event: any) => {
  const patient = event?.patient;
  const ehrId = ehrIdFromPlannerPatient(patient);
  return Boolean(
    patient &&
    ehrId &&
    (!hasValue(patient.language) ||
      !hasValue(patient.dob) ||
      (!hasValue(patient.mrn) && !hasValue(patient.accountNo))),
  );
};

const patientFallbackIdentityKey = (event: any) => {
  const patient = event?.patient || {};
  const canonicalName = normalizePatientNameParts(patient);
  const name =
    canonicalName.displayName ||
    canonicalName.patientName ||
    canonicalName.fullName ||
    cleanText(event?.patientName);
  // Canonicalize dob/mrn so two encounters of the same patient share a key
  // regardless of raw format ("1990-11-01" vs "11/1/1990", spaced/cased mrn) —
  // otherwise a resolved sibling's language never bridges to an unresolved row.
  const dob = formatDobShort(patient.dob);
  const mrn = cleanText(patient.mrn || patient.accountNo)
    .replace(/\s+/g, '')
    .toLowerCase();
  if (!name || (!dob && !mrn)) return '';
  return `patient:${name.toLowerCase()}|dob:${dob}|mrn:${mrn}`;
};

const plannerEventPatientIdentityKeys = (event: any) => {
  // Always include the demographic identity (name|dob|mrn) alongside any
  // structured keys, so recent events for the same patient share metadata even
  // when their local/EHR ids differ. plannerPatientIdentityKeys only emits the
  // fallback when NO structured key exists, which is too strict for this pass.
  const keys = plannerPatientIdentityKeys(event?.patient);
  const fallback = patientFallbackIdentityKey(event);
  if (fallback && !keys.includes(fallback)) keys.push(fallback);
  return keys;
};

const eventLocalPatientIds = (events: any[] = []) => {
  const ids = new Set<string>();
  events.forEach(event => {
    if (!plannerEventNeedsLocalPatientHydration(event)) return;
    const id = plannerEventLocalPatientId(event);
    if (id) ids.add(id);
  });
  return [...ids].sort();
};

const eventEhrPatientIdsNeedingLinkedLocalHydration = (events: any[] = []) => {
  const ids = new Set<string>();
  events.forEach(event => {
    const patient = event?.patient;
    if (!patient || plannerEventLocalPatientId(event)) return;
    const id = plannerEventEhrPatientId(event);
    if (id) ids.add(id);
  });
  return [...ids].sort();
};

const eventHydrationIds = (events: any[] = []) => {
  const ids = new Set<string>();
  events.forEach(event => {
    if (!plannerEventNeedsPatientHydration(event)) return;
    const id = plannerEventEhrPatientId(event);
    if (id) ids.add(id);
  });
  return [...ids].sort();
};

const fillPlannerPatientFromLocalPatient = (
  patient: PlannerPatient,
  localPatient: Record<string, any>,
): PlannerPatient => {
  const next: PlannerPatient = {...patient};
  const fill = (field: keyof PlannerPatient, value: any) => {
    if (!hasValue(next[field]) && hasValue(value)) {
      (next as any)[field] = value;
    }
  };

  fill('localId', localPatient.id);
  fill('firstName', localPatient.first_name || localPatient.firstName);
  fill('middleName', localPatient.middle_name || localPatient.middleName);
  fill('lastName', localPatient.last_name || localPatient.lastName);
  fill('language', patientLanguageFromRecord(localPatient));
  fill(
    'dialect',
    localPatient.preferred_dialect || localPatient.preferredDialect,
  );
  fill('mrn', localPatient.mrn || localPatient.medicalRecordNumber);
  fill(
    'dob',
    localPatient.date_of_birth ||
      localPatient.dateOfBirth ||
      localPatient.dob ||
      localPatient.birthDate,
  );
  fill('gender', localPatient.gender);
  fill(
    'phone',
    localPatient.phone_number || localPatient.phoneNumber || localPatient.phone,
  );
  fill('email', localPatient.email);

  const linkedEhrId = normalizeEhrId(
    localPatient.fhir_patient_id ||
      localPatient.ehr_patient_id ||
      localPatient.ehrPatientId ||
      localPatient._ehrPatientId ||
      localPatient._linkedEhrId,
  );
  fill('linkedEhrId', linkedEhrId);

  Object.assign(next, canonicalizePlannerPatientName(next));
  next.matchKind = canonicalMatchKind(next);

  return next;
};

export const applyLocalPatientToPlannerEvent = (
  event: any,
  localPatientsById: Record<string, any> = {},
) => {
  const id = plannerEventLocalPatientId(event);
  const localPatient = id ? localPatientsById[id] : null;
  if (!event?.patient || !localPatient) return event;

  const patient = fillPlannerPatientFromLocalPatient(
    event.patient,
    localPatient,
  );
  return {
    ...event,
    language: event.language || '',
    patient,
  };
};

export const applyLocalPatientsToPlannerEvents = (
  events: any[] = [],
  localPatientsById: Record<string, any> = {},
) => {
  if (!events.length || !Object.keys(localPatientsById).length) {
    return events;
  }
  return events.map(event =>
    applyLocalPatientToPlannerEvent(event, localPatientsById),
  );
};

export const applyLinkedLocalPatientsToPlannerEvents = (
  events: any[] = [],
  localPatientsByEhrId: Record<string, any> = {},
) => {
  if (!events.length || !Object.keys(localPatientsByEhrId).length) {
    return events;
  }
  return events.map(event => {
    const ehrId = plannerEventEhrPatientId(event);
    const localPatient = ehrId ? localPatientsByEhrId[ehrId] : null;
    if (!event?.patient || !localPatient) return event;

    const patient = fillPlannerPatientFromLocalPatient(
      event.patient,
      localPatient,
    );
    return {
      ...event,
      language: event.language || '',
      patient,
    };
  });
};

const fillPlannerPatientGaps = (
  patient: PlannerPatient,
  hydrated: Record<string, any>,
): PlannerPatient => {
  const next: PlannerPatient = {...patient};
  const fill = (field: keyof PlannerPatient, value: any) => {
    if (!hasValue(next[field]) && hasValue(value)) {
      (next as any)[field] = value;
    }
  };

  fill('language', patientLanguageFromRecord(hydrated));
  fill('dob', hydrated.dob || hydrated.dateOfBirth || hydrated.date_of_birth);
  fill('mrn', hydrated.mrn || hydrated.accountNo || hydrated.account_no);
  fill('accountNo', hydrated.accountNo || hydrated.account_no);
  fill('gender', hydrated.gender);
  fill('phone', hydrated.phone || hydrated.phoneNumber);
  fill('email', hydrated.email);
  fill('middleName', hydrated.middleName || hydrated.middle_name);
  fill('pcp', hydrated.pcp || hydrated.primaryCareProvider);
  Object.assign(next, canonicalizePlannerPatientName(next));
  next.matchKind = canonicalMatchKind(next);

  return next;
};

export const applyHydratedPatientToPlannerEvent = (
  event: any,
  hydratedPatientsById: Record<string, any> = {},
) => {
  const id = plannerEventEhrPatientId(event);
  const hydrated = id ? hydratedPatientsById[id] : null;
  if (!event?.patient || !hydrated) return event;

  const patient = fillPlannerPatientGaps(event.patient, hydrated);
  return {
    ...event,
    language: event.language || '',
    patient,
  };
};

export const applyHydratedPatientsToPlannerEvents = (
  events: any[] = [],
  hydratedPatientsById: Record<string, any> = {},
) => {
  if (!events.length || !Object.keys(hydratedPatientsById).length) {
    return events;
  }
  return events.map(event =>
    applyHydratedPatientToPlannerEvent(event, hydratedPatientsById),
  );
};

const sharedPatientMetadataFields = [
  'language',
  'dob',
  'mrn',
  'accountNo',
] as const;

export const applySharedPatientMetadataToPlannerEvents = (
  events: any[] = [],
) => {
  if (events.length < 2) return events;
  const metadataByKey: Record<string, Partial<PlannerPatient>> = {};

  events.forEach(event => {
    const patient = event?.patient;
    if (!patient) return;
    const keys = plannerEventPatientIdentityKeys(event);
    if (!keys.length) return;
    const metadata = sharedPatientMetadataFields.reduce(
      (next, field) =>
        hasValue(patient[field])
          ? {
              ...next,
              [field]: patient[field],
            }
          : next,
      {} as Partial<PlannerPatient>,
    );
    if (!Object.keys(metadata).length) return;
    keys.forEach(key => {
      metadataByKey[key] = {
        ...metadata,
        ...metadataByKey[key],
      };
    });
  });

  if (!Object.keys(metadataByKey).length) return events;

  return events.map(event => {
    const patient = event?.patient;
    if (!patient) return event;
    const keys = plannerEventPatientIdentityKeys(event);
    if (!keys.length) return event;
    const sharedMetadata = keys.reduce(
      (next, key) => ({
        ...next,
        ...(metadataByKey[key] || {}),
      }),
      {} as Partial<PlannerPatient>,
    );
    const patientUpdates = sharedPatientMetadataFields.reduce(
      (next, field) =>
        !hasValue(patient[field]) && hasValue(sharedMetadata[field])
          ? {
              ...next,
              [field]: sharedMetadata[field],
            }
          : next,
      {} as Partial<PlannerPatient>,
    );
    if (!Object.keys(patientUpdates).length) return event;
    return {
      ...event,
      patient: {
        ...patient,
        ...patientUpdates,
      },
    };
  });
};

const applyPatientMetadataTextToPlannerEvents = (events: any[] = []) =>
  events.map(event =>
    event && Object.prototype.hasOwnProperty.call(event, 'metadataText')
      ? {
          ...event,
          metadataText: plannerPatientMetadataText(event.patient),
        }
      : event,
  );

export default function useHydratedPlannerPatientEvents(
  events: any[] = [],
  companyId: any,
  enabled = true,
  {facilityId = null}: Record<string, any> = {},
) {
  const [localPatientsById, setLocalPatientsById] = useState<
    Record<string, any>
  >({});
  const [linkedLocalPatientsByEhrId, setLinkedLocalPatientsByEhrId] = useState<
    Record<string, any>
  >({});
  const [hydratedPatientsById, setHydratedPatientsById] = useState<
    Record<string, any>
  >({});
  const idsNeedingLocalHydration = useMemo(
    () => (enabled ? eventLocalPatientIds(events) : []),
    [enabled, events],
  );
  const localHydrationKey = idsNeedingLocalHydration.join('|');

  useEffect(() => {
    if (!localHydrationKey) return undefined;
    const ids = localHydrationKey.split('|').filter(Boolean);
    let cancelled = false;

    fetchPlannerLocalPatientsByIds(ids)
      .then((map: Record<string, any>) => {
        if (cancelled || !map) return;
        const entries = Object.entries(map);
        if (!entries.length) return;
        setLocalPatientsById(previous => {
          const hasChanges = entries.some(
            ([id, patient]) => previous[id] !== patient,
          );
          return hasChanges ? {...previous, ...map} : previous;
        });
      })
      .catch(() => {
        /* best-effort; keep rendering the denormalized planner row */
      });

    return () => {
      cancelled = true;
    };
  }, [localHydrationKey]);

  const locallyHydratedEvents = useMemo(
    () => applyLocalPatientsToPlannerEvents(events, localPatientsById),
    [events, localPatientsById],
  );
  const linkedLocalHydrationIds = useMemo(
    () =>
      enabled
        ? eventEhrPatientIdsNeedingLinkedLocalHydration(locallyHydratedEvents)
        : [],
    [enabled, locallyHydratedEvents],
  );
  const linkedLocalHydrationKey = linkedLocalHydrationIds.join('|');

  useEffect(() => {
    if (!linkedLocalHydrationKey) return undefined;
    const ids = linkedLocalHydrationKey.split('|').filter(Boolean);
    let cancelled = false;

    fetchPlannerLinkedLocalPatientsByEhrIds(ids, {companyId, facilityId})
      .then((map: Record<string, any>) => {
        if (cancelled || !map) return;
        const entries = Object.entries(map);
        if (!entries.length) return;
        setLinkedLocalPatientsByEhrId(previous => {
          const hasChanges = entries.some(
            ([id, patient]) => previous[id] !== patient,
          );
          return hasChanges ? {...previous, ...map} : previous;
        });
      })
      .catch(() => {
        /* best-effort; keep rendering the denormalized planner row */
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, facilityId, linkedLocalHydrationKey]);

  const identityHydratedEvents = useMemo(
    () =>
      applyLinkedLocalPatientsToPlannerEvents(
        locallyHydratedEvents,
        linkedLocalPatientsByEhrId,
      ),
    [locallyHydratedEvents, linkedLocalPatientsByEhrId],
  );
  const idsNeedingHydration = useMemo(
    () =>
      enabled && companyId ? eventHydrationIds(identityHydratedEvents) : [],
    [companyId, enabled, identityHydratedEvents],
  );
  const hydrationKey = idsNeedingHydration.join('|');

  useEffect(() => {
    if (!companyId || !hydrationKey) return undefined;
    const ids = hydrationKey.split('|').filter(Boolean);
    let cancelled = false;

    resolvePlannerEhrPatients(companyId, ids)
      .then((map: Record<string, any>) => {
        if (cancelled || !map) return;
        const entries = Object.entries(map);
        if (!entries.length) return;
        setHydratedPatientsById(previous => {
          const hasChanges = entries.some(
            ([id, patient]) => previous[id] !== patient,
          );
          return hasChanges ? {...previous, ...map} : previous;
        });
      })
      .catch(() => {
        /* best-effort; keep rendering the denormalized planner row */
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, hydrationKey]);

  return useMemo(
    () =>
      applyPatientMetadataTextToPlannerEvents(
        applySharedPatientMetadataToPlannerEvents(
          applyHydratedPatientsToPlannerEvents(
            identityHydratedEvents,
            hydratedPatientsById,
          ),
        ),
      ),
    [identityHydratedEvents, hydratedPatientsById],
  );
}
